import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalList,
  extractAdminPortalMessage,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import {
  showErrorMsg,
  showSuccessMsg,
  uploadFileWithProgress,
} from "../../../../utils/Helpers";
import {
  deleteAdminCompanyUser,
  getAdminCompanyUser,
  updateAdminCompanyUser,
} from "../../../../network/apis/adminCompanyUsers";
import { getAdminCompanyRoles } from "../../../../network/apis/adminCompanyRoles";
import {
  buildFullInternationalPhone,
  buildUpdateEmployeePayload,
  createEmployeeEditBaseline,
  extractCompanyUserPayload,
  isEmployeeEditDirty,
  mergeEmployeeRecord,
  normalizeCompanyUser,
  resolveCompanyRoleOption,
  resolveCompanyUserId,
} from "../../../Company-portal/TeamsPermissions/employeeMappers";
import { normalizeCompanyRoles } from "../../../Company-portal/TeamsPermissions/roleMappers";
import {
  adminCompanyTeamRoutes,
  COMPANY_DETAILS_TEAM_TAB,
  TEAM_SUB_TABS,
} from "../adminCompanyTeamRoutes";

const hydrateEditForm = (employee, roleOptions, setters) => {
  if (!employee) return;
  setters.setName(employee.name || "");
  setters.setPhone(
    buildFullInternationalPhone(employee.phoneNumber, employee.dialCode),
  );
  setters.setPhoneCountryDialCode(
    String(employee.dialCode || "966").replace(/\D/g, "") || "966",
  );
  setters.setPhoneCountryIso(employee.countryIso || "sa");
  setters.setRole(resolveCompanyRoleOption(employee, roleOptions));
  setters.setPictureFile(null);
  setters.setPicturePreview(employee.image || null);
  setters.setSubmitted(false);
  setters.setTouched({});
};

const useAdminViewEmployee = () => {
  const history = useHistory();
  const location = useLocation();
  const { companyId, id } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const teamCopy = messages[lang].teamManagement;
  const copy = teamCopy.viewEmployee;
  const formCopy = teamCopy.addEmployee;
  const companiesList = messages[lang].companiesList;
  const { shared } = messages[lang];
  const { canEdit, canDelete } = useAdminModulePermission(
    companyPermissionModules.employees,
  );

  const resolvedCompanyId = companyId || location.state?.companyId;
  const fallbackEmployee = location.state?.employeeData;
  const editFromListRef = useRef(Boolean(location.state?.startInEdit));
  const editBaselineRef = useRef(null);

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editing, setEditing] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryDialCode, setPhoneCountryDialCode] = useState("966");
  const [phoneCountryIso, setPhoneCountryIso] = useState("sa");
  const [role, setRole] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [pictureFile, setPictureFile] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [uploadState, setUploadState] = useState({ target: null, percent: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({});

  const goBackToList = useCallback(() => {
    if (!resolvedCompanyId) {
      history.push(ROUTE_PATHS.companiesList);
      return;
    }
    history.push(
      adminCompanyTeamRoutes.companyDetailsTabLocation(
        resolvedCompanyId,
        COMPANY_DETAILS_TEAM_TAB,
        location.state?.returnSubtab ?? TEAM_SUB_TABS.employees,
      ),
    );
  }, [history, resolvedCompanyId, location.state?.returnSubtab]);

  useEffect(() => {
    if (!id) {
      goBackToList();
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    getAdminCompanyUser(id)
      .then((userRes) => {
        if (cancelled) return;
        const payload = extractCompanyUserPayload(userRes);
        const normalized = normalizeCompanyUser(payload);
        if (!normalized.id) {
          if (resolveCompanyUserId(fallbackEmployee)) {
            setEmployee(fallbackEmployee);
          } else {
            throw new Error("Invalid employee payload");
          }
        } else {
          setEmployee(mergeEmployeeRecord(normalized, fallbackEmployee));
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setEmployee(null);
        showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
        goBackToList();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, goBackToList, shared.generalErrMsg, lang, fallbackEmployee]);

  useEffect(() => {
    if (!resolvedCompanyId) return undefined;

    let cancelled = false;
    getAdminCompanyRoles({ company_id: resolvedCompanyId, page: 1, items: 100 })
      .then((response) => {
        if (cancelled) return;
        setRoleOptions(normalizeCompanyRoles(extractAdminPortalList(response)));
      })
      .catch((error) => {
        if (cancelled) return;
        showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedCompanyId, shared.generalErrMsg, lang]);

  const enterEdit = useCallback(() => {
    if (!employee) return;
    hydrateEditForm(employee, roleOptions, {
      setName,
      setPhone,
      setPhoneCountryDialCode,
      setPhoneCountryIso,
      setRole,
      setPictureFile,
      setPicturePreview,
      setSubmitted,
      setTouched,
    });
    editBaselineRef.current = createEmployeeEditBaseline(employee, roleOptions);
    setEditing(true);
  }, [employee, roleOptions]);

  useEffect(() => {
    if (!editing || !employee || roleOptions.length === 0) return;
    setRole((current) => {
      if (current) return current;
      const resolved = resolveCompanyRoleOption(employee, roleOptions);
      if (resolved && editBaselineRef.current) {
        editBaselineRef.current = {
          ...editBaselineRef.current,
          roleId: String(resolved.id),
        };
      }
      return resolved;
    });
  }, [editing, employee, roleOptions]);

  const autoEnteredEditRef = useRef(false);
  useEffect(() => {
    if (
      employee &&
      location.state?.startInEdit &&
      !autoEnteredEditRef.current
    ) {
      autoEnteredEditRef.current = true;
      enterEdit();
    }
  }, [employee, location.state?.startInEdit, enterEdit]);

  const trimmedName = name.trim();
  const errors = useMemo(() => {
    const next = {};
    if (!trimmedName) next.name = formCopy.validations.nameRequired;
    if (!role) next.role = formCopy.validations.roleRequired;
    return next;
  }, [trimmedName, role, formCopy.validations]);

  const isFormValid = useMemo(
    () => Object.keys(errors).length === 0,
    [errors],
  );

  const isDirty = useMemo(
    () =>
      editing &&
      isEmployeeEditDirty(editBaselineRef.current, {
        name,
        phone,
        phoneCountryDialCode,
        role,
        pictureFile,
        picturePreview,
      }),
    [
      editing,
      name,
      phone,
      phoneCountryDialCode,
      role,
      pictureFile,
      picturePreview,
    ],
  );

  const showError = (field) =>
    Boolean((submitted || touched[field]) && errors[field]);

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const uploadingPicture = uploadState.target === "profile_picture";
  const busy = updating || uploadingPicture;

  const exitEdit = useCallback(() => {
    if (editFromListRef.current) {
      goBackToList();
      return;
    }
    setEditing(false);
  }, [goBackToList]);

  const cancelEdit = () => {
    if (busy) return;
    exitEdit();
  };

  const confirmDelete = async () => {
    if (!employee || deleting) return;
    setDeleting(true);
    try {
      const response = await deleteAdminCompanyUser(employee.id);
      const message = extractAdminPortalMessage(response);
      if (message) showSuccessMsg(message, true);
      else showSuccessMsg("deletedSuccessfully");
      goBackToList();
    } catch (error) {
      showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdate = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length || busy || !employee || !isDirty || !isFormValid)
      return;

    let profilePictureUrl;
    if (pictureFile) {
      try {
        profilePictureUrl = await uploadFileWithProgress(
          pictureFile,
          "profile_picture",
          "employee",
          setUploadState,
        );
      } finally {
        setUploadState({ target: null, percent: 0 });
      }
      if (!profilePictureUrl) return;
    }

    const data = buildUpdateEmployeePayload(editBaselineRef.current, {
      name: trimmedName,
      phone,
      phoneCountryDialCode,
      role,
      pictureFile,
      picturePreview,
      profilePictureUrl,
      employeeImage: employee.image,
    });

    if (Object.keys(data).length === 0) return;

    setUpdating(true);
    try {
      const response = await updateAdminCompanyUser({ id: employee.id, data });
      const updated = normalizeCompanyUser(extractCompanyUserPayload(response));
      setEmployee(updated);
      showSuccessMsg(copy.updateSuccess, true);
      exitEdit();
    } catch (error) {
      showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
    } finally {
      setUpdating(false);
    }
  };

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companiesList, label: companiesList.listBreadcrumb },
      {
        path: resolvedCompanyId
          ? adminCompanyTeamRoutes.companyDetailsTeamTab(
              resolvedCompanyId,
              location.state?.returnSubtab ?? TEAM_SUB_TABS.employees,
            )
          : "",
        label: companiesList.teamPermissionsTab,
      },
      { path: "", label: (employee || fallbackEmployee)?.name || copy.title },
    ],
    [
      companiesList.listBreadcrumb,
      companiesList.teamPermissionsTab,
      copy.title,
      resolvedCompanyId,
      employee,
      fallbackEmployee,
      location.state?.returnSubtab,
    ],
  );

  const resolvedEmployee = employee || fallbackEmployee || null;

  return {
    teamCopy,
    copy,
    formCopy,
    breadcrumbList,
    employee: resolvedEmployee,
    loading,
    editing,
    deleting,
    busy,
    confirmingDelete,
    setConfirmingDelete,
    canEdit,
    canDelete,
    name,
    setName,
    phone,
    setPhone,
    phoneCountryIso,
    setPhoneCountryDialCode,
    role,
    setRole,
    roleOptions,
    pictureFile,
    picturePreview,
    setPictureFile,
    setPicturePreview,
    uploadState,
    uploadingPicture,
    showError,
    markTouched,
    errors,
    isDirty,
    isFormValid,
    enterEdit,
    cancelEdit,
    confirmDelete,
    handleUpdate,
    closeDeleteModal: () => {
      if (deleting) return;
      setConfirmingDelete(false);
    },
  };
};

export default useAdminViewEmployee;
